import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { Icon } from '@launchpad-ui/icons';

import { useAgentInsights } from '../../AgentInsightsContext';
import { inferContextType } from '../../resources';
import type { Resource, ResourceOrQuery } from '../../resources';
import { trackVegaAiMentionOpened, trackVegaAiResourceAttached } from '../../vegaAnalytics';

import { getContextLabel, getResourceLabel } from './mentionUtils';
import { useResourceSearch } from './useResourceSearch';

import {
  mentionAttachedIcon,
  mentionDropdown,
  mentionEmptyText,
  mentionItem,
  mentionItemActive,
  mentionItemContent,
  mentionItemDescription,
  mentionItemDisabled,
  mentionItemLabel,
  mentionList,
  mentionSectionHeader,
  mentionSkeletonBar,
  mentionSkeletonBarShort,
} from './mentions.css';

interface ResourceTypeConfig {
  key: Resource;
  label: string;
  iconName: 'chart-dashboard' | 'notifications' | 'search';
}

const RESOURCE_TYPES: ResourceTypeConfig[] = [
  { key: 'Alert', label: 'Alerts', iconName: 'notifications' },
  { key: 'Dashboard', label: 'Dashboards', iconName: 'chart-dashboard' },
  { key: 'SavedSearch', label: 'Saved Searches', iconName: 'search' },
];

function getResultDescription(resource: ResourceOrQuery): string | undefined {
  const type = inferContextType(resource);
  const payload = resource?.payload ?? {};

  switch (type) {
    case 'Dashboard': {
      const graphCount = Array.isArray(payload?.graphs) ? payload.graphs.length : 0;
      return graphCount > 0 ? `${graphCount} graph${graphCount === 1 ? '' : 's'}` : undefined;
    }
    case 'Alert': {
      const parts = [payload?.product_type, payload?.disabled ? 'Disabled' : 'Active'].filter(Boolean);
      return parts.length > 0 ? parts.join(' · ') : undefined;
    }
    case 'SavedSearch': {
      const entityType = payload?.entity_type;
      const query = payload?.params?.query;
      const parts = [
        entityType ? `${entityType}s` : undefined,
        query ? (query.length > 40 ? `${query.substring(0, 39)}…` : query) : undefined,
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(' · ') : undefined;
    }
    default:
      return undefined;
  }
}

export interface MentionSuggestionPopoverProps {
  isOpen: boolean;
  position: { top: number; left: number } | null;
  /** Text typed after the @ trigger in the Tiptap editor — used to filter results. */
  query: string;
  command: ((attrs: { id: string; label: string; resourceType: string }) => void) | null;
  /** Deletes the typed query text from the editor, keeping the @ trigger intact. */
  onClearQuery: () => void;
  onClose: () => void;
  /** Called when a resource is selected via @-mention so the caller can store the payload. */
  onResourceAttached: (resource: ResourceOrQuery) => void;
}

function resourceItemId(resourceType: Resource, resourceId: string): string {
  return `${resourceType}--${resourceId}`;
}

function itemClasses(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

export const MentionSuggestionPopover: FC<MentionSuggestionPopoverProps> = ({
  isOpen,
  position,
  query,
  command,
  onClearQuery,
  onClose,
  onResourceAttached,
}) => {
  const { projectId } = useProjectId();
  const { context } = useAgentInsights();
  const { results, loading, search, clearResults } = useResourceSearch(projectId);

  const [selectedCategory, setSelectedCategory] = useState<Resource | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSearchKeyRef = useRef('');
  const hasTrackedOpen = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      trackVegaAiMentionOpened({});
      hasTrackedOpen.current = true;
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCategory(null);
      setActiveIndex(0);
      clearResults();
      lastSearchKeyRef.current = '';
    }
  }, [isOpen, clearResults]);

  useEffect(() => {
    if (!isOpen || !selectedCategory) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const key = `${selectedCategory}\0${query}`;
      if (key !== lastSearchKeyRef.current) {
        lastSearchKeyRef.current = key;
        search(selectedCategory, query);
      }
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, isOpen, search, selectedCategory]);

  useEffect(() => {
    setActiveIndex(0);
  }, [selectedCategory, results]);

  const filteredCategories = useMemo(() => {
    if (selectedCategory) {
      return [];
    }
    const lowerQuery = query.toLowerCase();
    return lowerQuery ? RESOURCE_TYPES.filter((rt) => rt.label.toLowerCase().includes(lowerQuery)) : RESOURCE_TYPES;
  }, [query, selectedCategory]);

  const flatIds = useMemo(() => {
    if (!selectedCategory) {
      return filteredCategories.map((rt) => rt.key);
    }
    return results.map((r) => resourceItemId(selectedCategory, r.id));
  }, [selectedCategory, filteredCategories, results]);

  useEffect(() => {
    if (!containerRef.current || flatIds.length === 0) {
      return;
    }
    const list = containerRef.current.querySelector('[role="listbox"]');
    const items = containerRef.current.querySelectorAll('[role="option"]');
    const el = items[activeIndex] as HTMLElement | undefined;
    if (!el || !list) {
      return;
    }
    const buffer = 4;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.bottom + buffer > listRect.bottom) {
      list.scrollTop += elRect.bottom - listRect.bottom + buffer;
    } else if (elRect.top - buffer < listRect.top) {
      list.scrollTop -= listRect.top - elRect.top + buffer;
    }
  }, [activeIndex, flatIds]);

  const attachedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of context.resourceOrQuery) {
      const type = inferContextType(r);
      ids.add(resourceItemId(type as Resource, r.id));
    }
    return ids;
  }, [context.resourceOrQuery]);

  const handleSelectResult = useCallback(
    (resource: ResourceOrQuery) => {
      if (!command || !selectedCategory) {
        return;
      }
      const itemId = resourceItemId(selectedCategory, resource.id);
      if (attachedIds.has(itemId)) {
        return;
      }
      const label = getContextLabel(resource);
      const resourceType = inferContextType(resource);
      command({ id: resource.id, label, resourceType });
      onResourceAttached(resource);
      trackVegaAiResourceAttached(resourceType);
    },
    [command, onResourceAttached, attachedIds, selectedCategory],
  );

  const handleSelectCategory = useCallback(
    (category: Resource) => {
      onClearQuery();
      setSelectedCategory(category);
      setActiveIndex(0);
      lastSearchKeyRef.current = `${category}\0`;
      search(category, '');
    },
    [search, onClearQuery],
  );

  const handleBack = useCallback(() => {
    setSelectedCategory(null);
    setActiveIndex(0);
    clearResults();
  }, [clearResults]);

  const activateItem = useCallback(
    (itemId: string) => {
      if (!selectedCategory) {
        const rt = RESOURCE_TYPES.find((r) => r.key === itemId);
        if (rt) {
          handleSelectCategory(rt.key);
        }
        return;
      }

      const prefix = `${selectedCategory}--`;
      if (itemId.startsWith(prefix)) {
        const rawId = itemId.slice(prefix.length);
        const resource = results.find((r) => r.id === rawId);
        if (resource) {
          handleSelectResult(resource);
        }
      }
    },
    [selectedCategory, results, handleSelectCategory, handleSelectResult],
  );

  const handleItemMouseEnter = useCallback(
    (index: number) => {
      if (index !== activeIndex) {
        setActiveIndex(index);
      }
    },
    [activeIndex],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (flatIds.length === 0) {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev + 1) % flatIds.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev - 1 + flatIds.length) % flatIds.length);
          break;
        case 'Enter':
          if (e.metaKey || e.ctrlKey) {
            break;
          }
        // falls through
        case 'Tab':
          e.preventDefault();
          if (flatIds[activeIndex]) {
            activateItem(flatIds[activeIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (selectedCategory) {
            handleBack();
          } else {
            onClose();
          }
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flatIds, activeIndex, activateItem, selectedCategory, handleBack, onClose]);

  if (!isOpen || !position) {
    return null;
  }

  const activeItemId = flatIds[activeIndex];
  const selectedCategoryConfig = selectedCategory
    ? RESOURCE_TYPES.find((rt) => rt.key === selectedCategory)
    : undefined;

  const dropdownStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: window.innerHeight - position.top + 4,
    left: position.left,
    zIndex: 9999,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- preventDefault keeps focus in Tiptap
    <div ref={containerRef} style={dropdownStyle} className={mentionDropdown} onMouseDown={(e) => e.preventDefault()}>
      {selectedCategory && <div className={mentionSectionHeader}>{selectedCategoryConfig?.label}</div>}

      <div role="listbox" aria-label="Resources" className={mentionList}>
        {!selectedCategory && (
          <>
            {filteredCategories.length === 0 ? (
              <div className={mentionEmptyText}>{`No categories matching “${query}”`}</div>
            ) : (
              filteredCategories.map((rt, i) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard nav handled by document-level keydown listener
                <div
                  key={rt.key}
                  role="option"
                  tabIndex={-1}
                  aria-selected={rt.key === activeItemId}
                  className={itemClasses(mentionItem, rt.key === activeItemId && mentionItemActive)}
                  onClick={() => handleSelectCategory(rt.key)}
                  onMouseEnter={() => handleItemMouseEnter(i)}
                >
                  <Icon name={rt.iconName} size="small" />
                  <div className={mentionItemContent}>
                    <span className={mentionItemLabel}>{rt.label}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {selectedCategory && loading && (
          <>
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className={mentionItem} aria-hidden>
                <div className={mentionItemContent}>
                  <div className={mentionSkeletonBar} />
                  <div className={mentionSkeletonBarShort} />
                </div>
              </div>
            ))}
          </>
        )}

        {selectedCategory && !loading && results.length === 0 && (
          <div className={mentionEmptyText}>{query ? `No results for “${query}”` : 'No resources found'}</div>
        )}

        {selectedCategory &&
          !loading &&
          results.map((resource, i) => {
            const itemId = resourceItemId(selectedCategory, resource.id);
            const description = getResultDescription(resource);
            const attached = attachedIds.has(itemId);
            const isActive = itemId === activeItemId;
            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard nav handled by document-level keydown listener
              <div
                key={itemId}
                role="option"
                tabIndex={-1}
                aria-selected={isActive}
                aria-disabled={attached || undefined}
                className={itemClasses(mentionItem, isActive && mentionItemActive, attached && mentionItemDisabled)}
                onClick={() => !attached && handleSelectResult(resource)}
                onMouseEnter={() => handleItemMouseEnter(i)}
              >
                <div className={mentionItemContent}>
                  <span className={mentionItemLabel}>
                    {getResourceLabel(resource)}
                    {attached && <Icon name="check-circle" size="small" className={mentionAttachedIcon} />}
                  </span>
                  {description && <span className={mentionItemDescription}>{description}</span>}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
